import { Datagrid, DateField, FunctionField, TextField, useTranslate } from 'react-admin'
import { parseTimestampToDate } from '../utils/activityTime'

// Enhanced component to render activity descriptions with JSON support for insulin activities
const ActivityDescriptionField = ({ record }: { record: any }) => {
  const translate = useTranslate()

  if (!record.description) {
    return <span>{translate('pos.common.empty_data')}</span>
  }

  // Try to parse as JSON for structured activities (insulin, food, exercise)
  try {
    const parsed = JSON.parse(record.description)

    if (typeof parsed === 'object' && parsed !== null) {
      // Handle insulin activities (check both record.type and parsed.type for compatibility)
      if (record.type === 'insulin' || parsed.type === 'insulin') {
        const { brand, customBrand, dosage, notes } = parsed
        const parts = []

        if (brand && brand !== 'custom') {
          parts.push(brand)
        }
        if (customBrand) {
          parts.push(customBrand)
        }
        if (dosage) {
          parts.push(`${dosage} ${translate('resources.activities.insulin.units')}`)
        }
        if (notes) {
          parts.push(`(${notes})`)
        }

        if (parts.length > 0) {
          return <span>{parts.join(' — ')}</span>
        }

        // If we parsed as insulin but got no parts, return empty data message instead of raw JSON
        return <span>{translate('pos.common.empty_data')}</span>
      }

      // Handle food activities
      if (
        record.type === 'food' ||
        record.type === 'feeding' ||
        parsed.type === 'food' ||
        parsed.type === 'feeding'
      ) {
        const { foodType, amount, unit } = parsed
        const parts = []

        if (foodType) parts.push(foodType)
        if (amount && unit) parts.push(`${amount} ${unit}`)
        else if (amount) parts.push(amount)

        if (parts.length > 0) {
          return <span>{parts.join(' — ')}</span>
        }

        // If we parsed as food but got no parts, return empty data message instead of raw JSON
        return <span>{translate('pos.common.empty_data')}</span>
      }

      // Handle exercise activities
      if (record.type === 'exercise' || parsed.type === 'exercise') {
        const { exerciseType, duration, intensity } = parsed
        const parts = []

        if (exerciseType) parts.push(exerciseType)
        if (duration) parts.push(duration)
        if (intensity) parts.push(`(${intensity})`)

        if (parts.length > 0) {
          return <span>{parts.join(' — ')}</span>
        }

        // If we parsed as exercise but got no parts, return empty data message instead of raw JSON
        return <span>{translate('pos.common.empty_data')}</span>
      }

      // Generic structured data fallback - filter out technical fields
      const technicalFields = ['type', 'id', 'pet', 'owner', 'device']
      const values = Object.entries(parsed)
        .filter(([key, value]) => !technicalFields.includes(key) && value != null && value !== '')
        .map(([_, value]) => value)

      if (values.length > 0) {
        return <span>{values.join(' — ')}</span>
      }

      // If we have JSON but no displayable values, show empty message instead of raw JSON
      return <span>{translate('pos.common.empty_data')}</span>
    }
  } catch (error) {
    // If JSON parsing fails, fall through to plain text
  }

  // Default: display as plain text (only for non-JSON descriptions)
  return <span>{record.description}</span>
}

const ActivitiyDatagrid = ({
  rowClick = false as
    | false
    | string
    | ((id: any, basePath: string, record: any) => string | Promise<string> | false),
}) => {
  const translate = useTranslate()
  return (
    <Datagrid rowClick={rowClick} bulkActionButtons={false}>
      <TextField source="id" />
      <FunctionField
        label={translate('resources.activities.fields.pet')}
        render={(record) => (typeof record.pet === 'object' ? record.pet.name : record.pet)}
      />
      <FunctionField
        label={translate('resources.activities.fields.owner')}
        render={(record) => (typeof record.owner === 'object' ? record.owner.name : record.owner)}
      />
      <TextField source="type" />
      <FunctionField
        label={translate('resources.activities.fields.start_time')}
        render={(record) => {
          const startTime = parseTimestampToDate(record.start_time)
          return startTime ? startTime.toLocaleString() : translate('pos.common.empty_data')
        }}
      />
      <FunctionField
        label={translate('resources.activities.fields.end_time')}
        render={(record) => {
          const endTime = parseTimestampToDate(record.end_time)
          return endTime ? endTime.toLocaleString() : translate('pos.common.empty_data')
        }}
      />
      <TextField source="time" label={translate('resources.activities.fields.duration')} />
      <FunctionField
        label={translate('resources.activities.fields.description')}
        render={(record) => <ActivityDescriptionField record={record} />}
      />
      <DateField source="created_at" label={translate('resources.activities.fields.created_at')} />
      <DateField source="updated_at" label={translate('resources.activities.fields.updated_at')} />
    </Datagrid>
  )
}

export default ActivitiyDatagrid
